// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import PropTypes from 'prop-types';

// Data
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useMatch } from 'react-router-dom';

// API/MODELS
import groupAPI from "services/api/groups";
import bksAPI from "services/api/bks";
import clearwaysAPI from "modules/clearways/api";

// Map
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';

// helpers
import { processDevicesData } from "utils/device";
import ClearwaysSidebar from "components/Sidenavs/ClearwaysSidebar";
import { processEventsData } from "utils/event";
import { useSoftUIController } from "hooks/context";
import { enableMap } from "utils/map";
import { processGroupData } from "utils/group";
import ClearwaysDevicesMap from "components/Maps/ClearwaysMaps/Devices";
import { bbox, bboxPolygon } from "@turf/turf";

import { setGroupPolygonColour } from "utils/map";

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function setGroupFeatures(group, showAdvancedDeviceLevel) {
    const { geojson, ...metadata } = group;

    var group_features = geojson;

    group_features.forEach((feature) => {
        feature.properties = {
            ...metadata,
            access: showAdvancedDeviceLevel,
            feature_colour: setGroupPolygonColour(group, showAdvancedDeviceLevel)
        };
    });

    return group_features;
}

function ClearwaysDevicesMapView({ map, user }) {
    useEffect(() => {
        if (!map.current) return;
        enableMap(map);
    }, []);

    groupAPI.loadToken(useAuth0());
    clearwaysAPI.loadToken(useAuth0());
    const [controller] = useSoftUIController();
    const { deviceLayout, miniNavbar, userPermissions } = controller;

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;
    const [hasFittedBounds, setHasFittedBounds] = useState(false);

    const { group_id, subgroup_id } = useMatch('overview/:group_id/:subgroup_id').params;

    const set_padding = 120;
    const getMapPadding = () => ({
        left: deviceLayout === 'mobile' ? set_padding / 2 : (miniNavbar ? 0 : 450) + set_padding,
        right: deviceLayout === 'mobile' ? set_padding / 2 : set_padding,
        top: deviceLayout === 'mobile' ? set_padding / 2 : 50 + set_padding,
        bottom: deviceLayout === 'mobile' ? set_padding / 2 : set_padding
    });

    const group = useQuery({
        queryKey: ['getGroupWithID', subgroup_id],
        queryFn: async () => {
            return await groupAPI.getGroupWithID(subgroup_id);
        }
    });

    const devices = useQuery({
        queryKey: ['getAllDevices'],
        queryFn: async () => {
            return await bksAPI.getAllDevices();
        }
    });

    const events = useQuery({
        queryKey: ['getAllClearways'],
        queryFn: async () => {
            return await clearwaysAPI.getAllClearways();
        }
    });

    const events_data = useMemo(() => {
        var processed = events.data ? processEventsData(events.data) : [];

        return processed.filter(function (event) {
            event.groups.forEach(function (group) {
                if (group.group_id == subgroup_id) {
                    return true;
                }
            });
            return false;
        });

    }, [events.data]);

    // console.log(group.data)

    const groups_data = useMemo(() => {
        if (typeof group.data != 'undefined') {
            return processGroupData(group.data, devices.data);
        }

        return null;

    }, [group.data, devices.data]);

    useEffect(() => {
        if (!map.current || !group.data) return;

        var bounds = bbox({
            "type": "FeatureCollection",
            "features": setGroupFeatures(groups_data, showAdvancedDeviceLevel)
        });
        var polygon = bboxPolygon(bounds);

        if (polygon) {
            var options = { padding: getMapPadding(), linear: true, duration: 800 };
            if (hasFittedBounds) {
                options.duration = 300;
            }

            map.current.fitBounds(polygon.bbox, options);
            setHasFittedBounds(true);
        }
    }, [map, groups_data, deviceLayout]);

    useEffect(() => {
        if (!map.current || !hasFittedBounds) return;

        map.current.easeTo({
            padding: getMapPadding(),
            duration: 800,
            essential: true
        });
    }, [miniNavbar, deviceLayout]);

    if (group.isLoading || devices.isLoading || events.isLoading) {
        return 'Loading...';
    }

    if (group.error || devices.error || events.error) {
        return "An error has occurred.";
    }

    if (!group.data || !devices.data) {
        return 'Loading...';
    }

    return (
        <SoftBox sx={{ width: '100%', height: '100%' }}>
            <ClearwaysDevicesMap {...{ map, user, groups_data }} />
            <ClearwaysSidebar
                user={user}
                groupPreviewID={subgroup_id}
                events={events_data}
            />
        </SoftBox>
    );
}

ClearwaysDevicesMapView.propTypes = {
    map: PropTypes.object,
    navTitle: PropTypes.array,
    setNavTitle: PropTypes.func,
    sidebarState: PropTypes.string,
    setSidebarState: PropTypes.func
}

export default ClearwaysDevicesMapView;